import {
  getCourses,
  getCurriculumsWithCourses,
  orEmptyArray,
} from '@lms/api-client/backend';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { LoaderCircle } from 'lucide-react';
import useSWR from 'swr';
import { useLocale, useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import AddCourseToCurriculum from '@/features/curriculums/components/add-course';
import AddCurriculum from '@/features/curriculums/components/add-curriculum';
import EditCurriculum from '@/features/curriculums/components/edit-curriculum';
import { OrderButtons } from '@/features/curriculums/components/order-buttons';
import { RemoveCourseButton } from '@/features/curriculums/components/remove-course-button';
import { RemoveCurriculumButton } from '@/features/curriculums/components/remove-curriculum-button';
import { takeLocaleString } from '@/lib/translate';

// Ports app/[brand]/admin/curriculums/page.tsx. The Next server component awaited
// the curriculums-with-courses and courses lists and read `user.superAdmin`;
// here both lists load via SWR and the super-admin gate comes from `useSiteAccess`.
// Every mutation revalidates the curriculums list via SWR `mutate` in place of
// `revalidatePath`. Renders CONTENT only; the admin `<Shell>` wraps this route.
export function Curriculums() {
  const locale = useLocale();
  const t = useTranslations('admin');
  const { brand: site, slug } = useBrand();
  const { access } = useSiteAccess(slug);
  const isSuperAdmin = access?.superAdmin ?? false;

  const { data: curriculums, mutate } = useSWR(
    ['admin/curriculums-with-courses', site.id],
    () => orEmptyArray(getCurriculumsWithCourses(site.id)),
  );
  const { data: courses } = useSWR(['admin/courses', site.id], () =>
    orEmptyArray(getCourses(site.id)),
  );

  if (curriculums === undefined || courses === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('curriculums')}</PageTitle>
        <PageButtons>
          <AddCurriculum locationId={site.id} onCreated={() => mutate()} />
        </PageButtons>
      </PageHeader>
      <div>
        {isSuperAdmin
          ? t('manage', { what: t('curriculums').toLowerCase() })
          : t('list', { what: t('curriculums').toLowerCase() })}
      </div>
      {curriculums.map((curriculum) => (
        <div key={curriculum.id} className="mt-4 border-t pt-4">
          <div className="flex flex-row">
            <h1 className="grow text-xl font-semibold">
              {takeLocaleString(
                locale,
                curriculum.nameTranslated ?? curriculum.name,
              )}
            </h1>
            <div className="flex flex-row gap-2">
              {isSuperAdmin && (
                <>
                  <EditCurriculum
                    curriculum={curriculum}
                    onSaved={() => mutate()}
                  />
                  <RemoveCurriculumButton
                    curriculumId={curriculum.id}
                    onRemoved={() => mutate()}
                  />
                </>
              )}
            </div>
          </div>
          <div className="flex flex-row flex-wrap items-start justify-start gap-8">
            {curriculum.courses
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((course) => (
                <Card
                  className="card bg-background w-96 shadow-xl"
                  key={course.id}
                >
                  <CardHeader>
                    <CardTitle>
                      {course.data[locale]?.name ||
                        course.data.en?.name ||
                        course.data[0]?.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex h-60 items-center justify-center overflow-clip rounded">
                      <img
                        src={course.image || '/placeholder.png'}
                        alt={t('course_image')}
                        className="object-cover"
                      />
                    </div>
                    {isSuperAdmin && (
                      <>
                        <RemoveCourseButton
                          className="mt-2 w-full"
                          courseId={course.id}
                          curriculumId={curriculum.id}
                          onRemoved={() => mutate()}
                        />
                        <OrderButtons
                          curriculum={curriculum}
                          course={course}
                          onMoved={() => mutate()}
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            {isSuperAdmin && (
              <div className="bg-background h-fit w-96 rounded-md border p-4">
                <AddCourseToCurriculum
                  curriculumId={curriculum.id}
                  courses={courses}
                  existingCourses={curriculum.courses}
                  onAdded={() => mutate()}
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </PageBox>
  );
}
